import React, { useEffect, useRef, useState } from 'react';

const skills = [
  {
    tag: 'Backend Engineering',
    title: 'Python & FastAPI Developers',
    desc: 'Our Python developers build APIs, background jobs, and integrations that hold up in production. They write tests, keep the code readable, and know FastAPI and Django well.',
    stack: ['Python', 'FastAPI', 'Django', 'Flask', 'Pydantic', 'Celery', 'SQLAlchemy', 'Asyncio'],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
        <line x1="12" y1="2" x2="12" y2="22"/>
      </svg>
    ),
  },
  {
    tag: 'Cloud Infrastructure',
    title: 'AWS Cloud Engineers',
    desc: 'Engineers who have set up and run real workloads on AWS. They can build your infrastructure from scratch or clean up what you already have, and they keep an eye on the monthly bill while doing it.',
    stack: ['EC2', 'Lambda', 'ECS / EKS', 'S3', 'RDS', 'API Gateway', 'CloudWatch', 'IAM'],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
      </svg>
    ),
  },
  {
    tag: 'DevOps & Containers',
    title: 'Docker & DevOps Engineers',
    desc: 'They put your apps in containers, set up CI/CD, and get deployments to a point where nobody is nervous on release day. Logging and alerts are part of the job too.',
    stack: ['Docker', 'Kubernetes', 'Terraform', 'GitHub Actions', 'CI/CD', 'Nginx', 'Prometheus', 'Grafana'],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
      </svg>
    ),
  },
  {
    tag: 'Architecture',
    title: 'System Design Experts',
    desc: 'Senior engineers for the big decisions. How to split services, which database to pick, where to cache, how to handle queues. Useful when you are planning something new or your current setup is starting to struggle.',
    stack: ['Microservices', 'Distributed Systems', 'Kafka', 'RabbitMQ', 'SQS', 'Redis', 'Caching', 'Scalability'],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="6" height="6" rx="1"/>
        <rect x="16" y="7" width="6" height="6" rx="1"/>
        <rect x="9" y="14" width="6" height="6" rx="1"/>
        <path d="M8 10h8M12 10v4"/>
      </svg>
    ),
  },
  {
    tag: 'AI Engineering',
    title: 'AI & LLM Developers',
    desc: 'Developers who build RAG systems, AI agents, and LLM features for real users. It is the same work we do for our own clients, so they have already dealt with the tricky parts.',
    stack: ['LLM APIs', 'LangChain', 'LangGraph', 'RAG', 'Vector DBs', 'AI Agents', 'Prompt Engineering'],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a4 4 0 0 1 4 4v1h1a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v-7a3 3 0 0 1 3-3h1V6a4 4 0 0 1 4-4z"/>
        <circle cx="9" cy="13" r="1"/><circle cx="15" cy="13" r="1"/>
        <path d="M9 17s1 1 3 1 3-1 3-1"/>
      </svg>
    ),
  },
  {
    tag: 'Full-Stack & Data',
    title: 'Full-Stack & Database Developers',
    desc: 'Developers who can take a feature from the database all the way to the screen on their own. A good fit when you want one person who can work across your whole codebase.',
    stack: ['React', 'Next.js', 'Node.js', 'TypeScript', 'PostgreSQL', 'MongoDB', 'GraphQL', 'REST'],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5" rx="9" ry="3"/>
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
      </svg>
    ),
  },
];

const models = [
  {
    name: 'Full-Time Dedicated',
    hours: '~160 hrs / month',
    desc: 'Works only on your product. Joins your standups, uses your tools, follows your sprint plan. Basically an in-house hire, without the recruiting.',
    points: ['Exclusive to your project', 'Follows your sprint plan', 'Best for long-term products'],
    featured: true,
  },
  {
    name: 'Part-Time',
    hours: '~80 hrs / month',
    desc: 'A few hours a day from an experienced developer. Works well for maintenance, steady feature work, or adding a skill your team is missing.',
    points: ['Flexible daily hours', 'Extra skills when you need them', 'Easy to scale up later'],
  },
  {
    name: 'Hourly / On-Demand',
    hours: 'Pay as you go',
    desc: 'Bring someone in for one specific job. An architecture review, an AWS setup, moving to Docker, or fixing a slow API.',
    points: ['No long-term commitment', 'Good for reviews and short tasks', 'You pay only for hours used'],
  },
  {
    name: 'Dedicated Team',
    hours: 'Custom team size',
    desc: 'A small team of developers, a DevOps engineer, and a tech lead, put together around your roadmap. We manage the delivery.',
    points: ['Tech lead included', 'Backend, cloud, and DevOps covered', 'Scale the team as you grow'],
  },
];

const steps = [
  {
    num: '01',
    title: 'Share Requirements',
    desc: 'Tell us the role, the skills you need, the experience level, and for how long. A short call is usually enough.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
  },
  {
    num: '02',
    title: 'Get Profiles',
    desc: 'We pick developers from our team whose experience fits your stack and send you their profiles.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    num: '03',
    title: 'Interview & Select',
    desc: 'You interview them. Run your own technical round if you want. You only go ahead with someone you are happy with.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    ),
  },
  {
    num: '04',
    title: 'Start Working',
    desc: 'The developer joins your Slack, your repo, and your standups and gets to work. We check in regularly to make sure things are going well.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
        <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
        <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
      </svg>
    ),
  },
];

const benefits = [
  {
    title: 'Tested Before You Meet Them',
    desc: 'We test every developer on practical problems before we put them in front of you. You only meet people who have worked on live products.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>
      </svg>
    ),
  },
  {
    title: 'No Hiring Overhead',
    desc: 'No job posts, no screening rounds, no payroll or HR paperwork on your side. We take care of that.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
  },
  {
    title: 'Scale Up or Down',
    desc: 'Add people when there is more work and reduce when there is less. No long notice periods.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
      </svg>
    ),
  },
  {
    title: 'Hours That Match Yours',
    desc: 'Our developers adjust their hours to overlap with your team, so standups and code reviews happen live, not a day later.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
  },
  {
    title: 'Your Code, Your IP',
    desc: 'We sign an NDA before we start. Everything is built in your repos and on your accounts, and all of it belongs to you.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    ),
  },
  {
    title: 'Backed by Slatewing',
    desc: 'You are not getting a lone freelancer. Our senior engineers review code and help with architecture whenever it is needed.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ),
  },
];

const roleOptions = skills.map(s => s.title);
const modelOptions = models.map(m => m.name);

const emptyForm = { name: '', email: '', company: '', role: '', model: '', team: '', message: '' };

const HireDevelopersPage: React.FC = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );
    pageRef.current?.querySelectorAll('.hire-group').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const pickRole = (role: string) => {
    setForm(p => ({ ...p, role }));
    scrollToForm();
  };

  const pickModel = (model: string) => {
    setForm(p => ({ ...p, model }));
    scrollToForm();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { name, email, company, role, model, team, message } = form;
    const subject = `Hiring request: ${role || 'Developer'}${company ? ` for ${company}` : ''}`;
    const body = [
      `Name: ${name}`,
      `Email: ${email}`,
      `Company: ${company}`,
      `Developer type: ${role}`,
      `Engagement model: ${model}`,
      `Number of developers: ${team}`,
      '',
      message,
    ].join('\n');
    window.location.href = `mailto:team@slatewing.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  return (
    <div className="svcpage" ref={pageRef}>
      {/* Page hero */}
      <div className="svcpage-hero">
        <div className="orb orb-1" style={{ opacity: 0.5 }} />
        <div className="orb orb-2" style={{ opacity: 0.4 }} />
        <div className="hero-grid" />
        <div className="container">
          <span className="section-label" style={{ justifyContent: 'center', display: 'flex', marginBottom: 16 }}>
            Hire Developers
          </span>
          <h1>
            Add Developers<br />
            <span className="gt">To Your Team</span>
          </h1>
          <p>
            Need more hands on your project? Hire Python, FastAPI, AWS, Docker,
            and system design developers from Slatewing. They work with your
            team, full-time, part-time, or by the hour.
          </p>
          <div className="hire-hero-ctas">
            <button className="btn btn-primary partner-btn" onClick={scrollToForm}>
              Hire a Developer
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
            <a href="#hire-skills" className="btn btn-ghost" onClick={(e) => { e.preventDefault(); document.getElementById('hire-skills')?.scrollIntoView({ behavior: 'smooth' }); }}>
              See Skills
            </a>
          </div>
          <div className="hire-hero-tags">
            {['Python', 'FastAPI', 'AWS', 'Docker', 'Kubernetes', 'System Design', 'LLMs & AI', 'React'].map(t => (
              <span className="tech-tag" key={t}>{t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Skills */}
      <section className="hire-section hire-group" id="hire-skills">
        <div className="container">
          <div className="section-header reveal" style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 56px' }}>
            <span className="section-label" style={{ justifyContent: 'center' }}>Skills</span>
            <h2>Who you can<br /><span className="gt">hire from us</span></h2>
            <p style={{ margin: '0 auto', textAlign: 'center' }}>
              Hire one developer or a few with different skills. Whatever your project needs.
            </p>
          </div>

          <div className="hire-skills-grid">
            {skills.map((s, i) => (
              <div className={`svcpage-card hire-skill-card reveal d${(i % 3) + 1}`} key={s.title}>
                <div className="svcpage-card-glow" />
                <div className="svcpage-icon">{s.icon}</div>
                <span className="svcpage-card-tag">{s.tag}</span>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <div className="tech-tags">
                  {s.stack.map(t => <span className="tech-tag" key={t}>{t}</span>)}
                </div>
                <button className="hire-card-link" onClick={() => pickRole(s.title)}>
                  Hire for this role
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Engagement models */}
      <section className="hire-section hire-group">
        <div className="container">
          <div className="section-header reveal" style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 56px' }}>
            <span className="section-label" style={{ justifyContent: 'center' }}>Engagement Models</span>
            <h2>Pick how you<br /><span className="gt">want to work</span></h2>
            <p style={{ margin: '0 auto', textAlign: 'center' }}>
              Choose what fits your budget and timeline. You can change it later if your needs change.
            </p>
          </div>

          <div className="hire-models-grid">
            {models.map((m, i) => (
              <div className={`hire-model-card${m.featured ? ' featured' : ''} reveal d${i + 1}`} key={m.name}>
                {m.featured && <span className="hire-model-badge">Most Popular</span>}
                <h3 className="hire-model-name">{m.name}</h3>
                <div className="hire-model-hours">{m.hours}</div>
                <p className="hire-model-desc">{m.desc}</p>
                <ul className="svcpage-features">
                  {m.points.map(pt => <li key={pt}>{pt}</li>)}
                </ul>
                <button
                  className={`btn ${m.featured ? 'btn-primary' : 'btn-ghost'} hire-model-btn`}
                  onClick={() => pickModel(m.name)}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Hiring process */}
      <section className="hire-section hire-group">
        <div className="container">
          <div className="section-header reveal" style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 64px' }}>
            <span className="section-label" style={{ justifyContent: 'center' }}>How Hiring Works</span>
            <h2>Four simple steps<br /><span className="gt">to get started</span></h2>
            <p style={{ margin: '0 auto', textAlign: 'center' }}>You decide who joins your team. We handle the rest.</p>
          </div>

          <div className="process-grid">
            {steps.map((step, i) => (
              <div className={`process-card reveal d${i + 1}`} key={step.num}>
                <div className="process-num-bg">{step.num}</div>
                <div className="process-top">
                  <span className="process-badge">{step.num}</span>
                  <div className="process-icon">{step.icon}</div>
                </div>
                <h3 className="process-title">{step.title}</h3>
                <p className="process-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Benefits */}
      <section className="hire-section hire-group">
        <div className="container">
          <div className="section-header reveal" style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 64px' }}>
            <span className="section-label" style={{ justifyContent: 'center' }}>Why Slatewing</span>
            <h2>Why hire<br /><span className="gt">through us</span></h2>
          </div>

          <div className="whyus-grid">
            {benefits.map((b, i) => (
              <div className={`whyus-card reveal d${(i % 3) + 1}`} key={b.title}>
                <div className="whyus-icon">{b.icon}</div>
                <h3 className="whyus-title">{b.title}</h3>
                <p className="whyus-desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Inquiry form */}
      <section className="hire-section hire-group" id="hire-form">
        <div className="container">
          <div className="contact-inner">
            <div className="contact-info">
              <div className="reveal">
                <span className="section-label">Hire Developers</span>
                <h2>Tell us who<br /><span className="gt">you need</span></h2>
              </div>
              <p className="reveal d1">
                Fill this in and we will send you developer profiles that match.
                Nothing is final until you have interviewed them and picked
                someone.
              </p>
              <ul className="svcpage-features reveal d2">
                <li>Python, AWS, Docker and system design developers</li>
                <li>You interview them before you decide</li>
                <li>Full-time, part-time, hourly, or a full team</li>
                <li>NDA signed and the code is yours</li>
              </ul>
            </div>

            <div className="contact-form reveal d2" ref={formRef} style={{ scrollMarginTop: 100 }}>
              {submitted ? (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: 'var(--gradient)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 20px',
                    boxShadow: '0 8px 32px color-mix(in srgb, var(--brand) 35%, transparent)',
                    fontSize: '1.4rem', color: 'var(--bg)', fontWeight: 700,
                  }}>✓</div>
                  <h3 style={{ marginBottom: 10 }}>Request sent!</h3>
                  <p>We will get back to you with profiles shortly.</p>
                  <button className="btn btn-ghost" style={{ marginTop: 24 }} onClick={() => { setSubmitted(false); setForm(emptyForm); }}>
                    Send another request
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} noValidate>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label" htmlFor="h-name">Name</label>
                      <input id="h-name" name="name" type="text" className="form-input" placeholder="Your name" value={form.name} onChange={onChange} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="h-email">Work Email</label>
                      <input id="h-email" name="email" type="email" className="form-input" placeholder="you@company.com" value={form.email} onChange={onChange} required />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label" htmlFor="h-company">Company</label>
                      <input id="h-company" name="company" type="text" className="form-input" placeholder="Company name" value={form.company} onChange={onChange} />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="h-team">Developers Needed</label>
                      <select id="h-team" name="team" className="form-input form-select" value={form.team} onChange={onChange}>
                        <option value="">Select</option>
                        <option value="1">1</option>
                        <option value="2 to 3">2 to 3</option>
                        <option value="4 to 5">4 to 5</option>
                        <option value="6+">6+</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label" htmlFor="h-role">Developer Type</label>
                      <select id="h-role" name="role" className="form-input form-select" value={form.role} onChange={onChange}>
                        <option value="">Select a role</option>
                        {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="h-model">Engagement Model</label>
                      <select id="h-model" name="model" className="form-input form-select" value={form.model} onChange={onChange}>
                        <option value="">Select a model</option>
                        {modelOptions.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="h-message">Project Details</label>
                    <textarea id="h-message" name="message" className="form-input" placeholder="Tech stack, experience level, start date, how long you need them" value={form.message} onChange={onChange} required />
                  </div>
                  <button type="submit" className="btn btn-primary form-submit">
                    Request Developers
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HireDevelopersPage;
